import mainStyle from './port.module.scss';
import { ReactComponent as ProfileIcon } from "assets/images/icons/icn_dashboard_profile.svg";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";
import { useNavigate, useLocation } from "react-router-dom";

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, isEqual } from "lodash";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Component
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { RadioButton } from 'components/buttons/RadioButton';
import GlobalAlert from "components/Notification";
import Loading from "./loading";
import RemoveButton from './removeButton';
import BottomActionBar from "./bottomActionBar";
import ConfirmDeleteModal from "./confirmDeleteModal";
import LeaveConfirmModal from "./leaveConfirmModal";
import MacWhitelistSection from "./macWhitelistSection";
import RadiusProfileSection from "./radiusProfileSection";
import LinkModal from "components/LinkModal";

// Store & Icons
import useZustandStore from "zustandStore";
import { ReactComponent as ProColorIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

// Util
import {
  defaultValue,
  deviceCategoryOption,
  portTypeOption,
  linkSpeedOption,
  apiMappingKey,
  defaultAccessPolicyTypeList,
  accessPolicyTypeDefinition,
} from "./util/const";
import Api from "./util/api";
import Form from "./util/form";
import { initScheduleOptions } from "./util/func";

// Const
import { formLimitation } from 'const';

const proIconStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: 2,
  marginTop: -12,
  marginBottom: -12,
  flexShrink: 0,
};

const PortContent = ({ auth, selectedPort = null, showEditGeneral, onDrawerApplyCallback = () => { } }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const api = Api.init(auth);
  const form = Form.init(theme);
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);

  // State
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deviceType, setDeviceType] = useState(null);
  const [originFormValue, setOriginFormValue] = useState(null);
  const [formValue, setFormValue] = useState(null);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);
  const [leaveProfileType, setLeaveProfileType] = useState("schedule"); // schedule, radius-server, mac-address
  const [leaveModalTitle, setLeaveModalTitle] = useState("");
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [radiusProfiles, setRadiusProfiles] = useState([]);
  const [macProfiles, setMacProfiles] = useState([]);
  const [uuidList, setUuidList] = useState({
    "update": uuidv4(),
    "delete": uuidv4(),
    "readSchedule": uuidv4(),
    "readRadiusProfile": uuidv4(),
    "readMacProfile": uuidv4(),
  });
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const radiusProfileOptions = useMemo(() => {
    return radiusProfiles
      .filter((item) => item.id && item.name)
      .map(({ id, name }) => ({
        value: id,
        label: name,
      }));
  }, [radiusProfiles]);

  const macProfileOptions = useMemo(() => {
    return macProfiles
      .filter((item) => item.id && item.name)
      .map((item) => ({
        value: item.id,
        label: item.name,
        macCount: item.macList?.length ?? item.macAddresses?.length ?? 0,
      }));
  }, [macProfiles]);

  // Method
  const fetchTableData = () => {
    const { apiKey, params } = api.getScheduleRequestParams(Api.actionTypeEnum.read);
    sendRequest(
      apiKey,
      uuidList.readSchedule,
      params,
    );
  };

  const fetchRadiusProfiles = () => {
    const { apiKey, params } = api.getRadiusProfileRequestParams(Api.actionTypeEnum.read);
    sendRequest(apiKey, uuidList.readRadiusProfile, params);
  };

  const fetchMacProfiles = () => {
    const { apiKey, params } = api.getMacProfileRequestParams(Api.actionTypeEnum.read);
    sendRequest(apiKey, uuidList.readMacProfile, params);
  };

  const handlePortScheduleChange = (opt) => {
    if (opt.value === "CREATE_NEW") {
      setLeaveProfileType("schedule");
      setLeaveModalTitle("Create New Schedule");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("portSchedule", opt.value);
    }
  };

  const handlePoeScheduleChange = (opt) => {
    if (opt.value === "CREATE_NEW") {
      setLeaveProfileType("schedule");
      setLeaveModalTitle("Create New Schedule");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("poeSchedule", opt.value);
    }
  };

  const handleRadiusProfileChange = (opt) => {
    if (opt.value === "Edit/Create New Profile") {
      setLeaveProfileType("radius-server");
      setLeaveModalTitle("Create New RADIUS Profile");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("accessPolicy", {
        ...formValue.accessPolicy,
        radiusProfile: opt.value === "" ? null : opt.value,
      });
    }
  };

  const handleMacProfileChange = (opt) => {
    if (opt.value === "Edit/Create New Profile") {
      setLeaveProfileType("mac-address");
      setLeaveModalTitle("Create New MAC Address Profile");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("accessPolicy", {
        ...formValue.accessPolicy,
        macList: opt.value === "" ? null : opt.value,
      });
    }
  };

  const handleLeaveConfirm = () => {
    setOpenLeaveModal(false);
    let targetTab = "profiles-schedule";
    if (leaveProfileType === "radius-server") {
      targetTab = "profiles-radius-server";
    } else if (leaveProfileType === "mac-address") {
      targetTab = "profiles-mac-address";
    }
    navigate(location.pathname, {
      state: { navigateTab: targetTab },
    });
  };

  const getDeviceTypeLabel = (deviceCategory) => {
    switch (deviceCategory) {
      case deviceCategoryOption.switch.value:
        return deviceCategoryOption.switch.label;
      case deviceCategoryOption.ap.value:
        return deviceCategoryOption.ap.label;
      default:
        return "";
    }
  }

  const handleChangeFormValue = (key, value) => {
    setFormValue((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  const handleChangeMultiFormValues = ({ updateData }) => {
    setFormValue((prev) => ({
      ...prev,
      ...updateData,
    }));
  }

  const handleHoverDeleteButton = (isHovered) => {
    setIsDeleteHovered(isHovered);
  }

  const handleOpenRemoveModal = () => {
    setOpenRemoveModal(true);
  }

  const resetFormValue = () => {
    setFormValue(cloneDeep(originFormValue));
  }

  const checkDataNotChanged = () => {
    let flag = true;
    for (const key in apiMappingKey) {
      const origin = originFormValue?.[key];
      const current = formValue?.[key];
      const isObjProps = typeof origin === "object" && origin !== null;

      const originValue = isObjProps ? origin : (origin?.toString() ?? "");
      const currentValue = isObjProps ? current : (current?.toString() ?? "");

      if (!isEqual(originValue, currentValue)) {
        flag = false;
        break;
      }
    }
    return flag;
  }


  const checkFormValid = () => {
    if (!formValue.profileName || formValue.profileName.trim() === "") {
      return false;
    }

    if (formValue.portType === portTypeOption.trunk.value
      && (
        formValue.allowVlan?.trim().length === 0
        || !form.checkAllowVlans(formValue.allowVlan?.trim())
      )
    ) {
      return false;
    }

    if (!form.checkPvid(formValue.pvid?.toString().trim())) {
      return false;
    }

    if (formValue.portType === portTypeOption.access.value && formValue.accessPolicy?.enableState) {
      const isStaticType = formValue.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value;
      const isDeleteOnTimeType = formValue.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value;
      const is802ProfileType = formValue.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value;

      if (isStaticType || isDeleteOnTimeType) {
        if (!formValue.accessPolicy.macList) return false;
      }

      if (isDeleteOnTimeType) {
        if (!form.checkDynamicSize(formValue.accessPolicy?.dynamicSize)) {
          return false;
        }
      }

      if (is802ProfileType) {
        if (!formValue.accessPolicy.radiusProfile) return false;
      }
    }

    return true;
  }

  const handleApply = () => {
    if (!formValue) return;

    if (!isUnityProSubscribed) {
      const isSwitch = (deviceType || formValue.deviceCategory) === deviceCategoryOption.switch.value;
      const isAccessPort = isSwitch && formValue.portType === portTypeOption.access.value;
      const isAccessPolicyEnabled = formValue.accessPolicy?.enableState === true;

      if (isAccessPort && isAccessPolicyEnabled) {
        setIsLinkModalOpen(true);
        return;
      }
    }

    setLoading(true);
    const payload = defaultValue.makePayload(formValue);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, formValue.id);
    sendRequest(
      apiKey,
      uuidList.update,
      params,
      payload,
    );
  }

  const handleDelete = () => {
    setOpenRemoveModal(false);
    setLoading(true);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.delete, formValue.id);
    sendRequest(
      apiKey,
      uuidList.delete,
      params,
    );
  }

  // Side effect
  useEffect(() => {
    if (!auth.scopeLevel) return;
    fetchTableData();
    fetchRadiusProfiles();
    fetchMacProfiles();
  }, [auth.scopeLevel]);

  useEffect(() => {
    const readResponse = getData(uuidList.readSchedule);
    if (readResponse) {
      const initializedOptions = initScheduleOptions({ scheduleList: readResponse?.data || [] });
      setScheduleOptions(initializedOptions);
    }
  }, [dataMap?.[uuidList.readSchedule]]);

  useEffect(() => {
    const readResponse = getData(uuidList.readRadiusProfile);
    if (readResponse && readResponse.data) {
      setRadiusProfiles(readResponse.data);
    }
  }, [dataMap?.[uuidList.readRadiusProfile]]);

  useEffect(() => {
    const readResponse = getData(uuidList.readMacProfile);
    if (readResponse && readResponse.data) {
      setMacProfiles(readResponse.data);
    }
  }, [dataMap?.[uuidList.readMacProfile]]);

  useEffect(() => {
    if (selectedPort) {
      const updatedPort = {};
      for (const key in apiMappingKey) {
        const apiKey = apiMappingKey[key];
        updatedPort[key] = selectedPort[apiKey];
      }

      // Backward compatible for fiberLinkSpeed
      if (!updatedPort.fiberLinkSpeed) {
        updatedPort.fiberLinkSpeed = linkSpeedOption.auto.value;
      }

      const rawPolicy = selectedPort.accessPolicy;
      let initializedRadiusProfile = null;
      if (rawPolicy?.radiusProfile) {
        if (Array.isArray(rawPolicy.radiusProfile)) {
          initializedRadiusProfile = rawPolicy.radiusProfile[0] || null;
        } else {
          initializedRadiusProfile = rawPolicy.radiusProfile || null;
        }
      }

      let initializedMacList = null;
      if (rawPolicy?.macList) {
        if (Array.isArray(rawPolicy.macList)) {
          if (rawPolicy.macList[0] && typeof rawPolicy.macList[0] === 'object') {
            initializedMacList = null;
          } else {
            initializedMacList = rawPolicy.macList[0] || null;
          }
        } else {
          initializedMacList = rawPolicy.macList || null;
        }
      }

      updatedPort.accessPolicy = {
        enableState: rawPolicy?.enableState ?? false,
        type: rawPolicy?.type ?? accessPolicyTypeDefinition.STATIC.value,
        macList: initializedMacList,
        radiusProfile: initializedRadiusProfile,
        dynamicSize: rawPolicy?.dynamicSize ?? null,
      };

      updatedPort.isPortScheduleMode = !!selectedPort.portSchedule;
      updatedPort.isPoeScheduleMode = !!selectedPort.poeSchedule;

      setOriginFormValue(cloneDeep(updatedPort));
      setFormValue(cloneDeep(updatedPort));
      setDeviceType(selectedPort.deviceCategory);
      setLoading(true);
    } else {
      setFormValue(null);
    }
  }, [selectedPort]);

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setLoading(false);
      }, 0);
    }
  }, [loading]);

  useEffect(() => {
    if (!dataMap?.[uuidList.update]) return;

    const updateResponse = getData(uuidList.update);
    if (updateResponse) {
      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onDrawerApplyCallback();
      }
    }
  }, [dataMap?.[uuidList.update]]);

  useEffect(() => {
    if (!dataMap?.[uuidList.delete]) return;

    const deleteResponse = getData(uuidList.delete);;
    if (deleteResponse) {
      if (deleteResponse.hasOwnProperty("code") && deleteResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: deleteResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onDrawerApplyCallback();
      }
    }
  }, [dataMap?.[uuidList.delete]]);

  const containerId = `profilePortContainer_${selectedPort?.id || "default"}`;

  return (
    <Box>
      {loading ? (
        <Loading />
      ) : (
        <>
          <Box id={containerId} sx={form.getFormStyle()}>
            <Box sx={form.getFormHeaderStyle()}>
              <ProfileIcon />
              <Box sx={{ marginLeft: '10px' }}>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[5],
                    fontWeight: theme.typography.customFontWeight[3],
                  }}>
                  {formValue ? formValue.profileName : ""}
                </Typography>
                <Box display={'flex'} alignItems="center">
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      border: `1.5px solid ${theme.palette.primary.main}`,
                      padding: "4px 12px",
                      height: "27px",
                      fontSize: theme.typography.customFontSize[0],
                      borderRadius: theme.shape.customBorderRadius[7],
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      display: "flex",
                    }}
                  >
                    {getDeviceTypeLabel(formValue.deviceCategory)}
                  </Box>
                  <Box display={'flex'} alignItems={'center'} gap={1} sx={{ marginLeft: '10px' }}>
                    <Box>Assigned</Box>
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        ":hover": {
                          textDecoration: 'none',
                        },
                      }}
                      onClick={() => {
                        navigate("/site/settings", {
                          state: {
                            navigateTab: "wired-port-management",
                            navigatePortProfileName: formValue.profileName || "",
                            navigatePortDeviceType: deviceCategoryOption[deviceType].tabName.portManagement
                          }
                        });
                      }}
                    >
                      {formValue.assignedPort?.length} Port(s)
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{
              width: "100%",
              color: theme.palette.primary.greySecound,
              fontSize: theme.typography.customFontSize[2],
              pt: 2,
              pl: "20px"
            }}>
              ✦ Available features may vary depending on the device model
            </Box>

            <Box sx={{
              ...form.getFormBodyStyle(),
              pt: 0,
            }}>
              {
                deviceType === deviceCategoryOption.switch.value && (
                  <>
                    <CollapseCard title="Basic" >
                      <Box className={mainStyle["section-container"]}>
                        {/* Profile Name */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Profile Name
                            </Typography>
                            <TextField
                              placeholder="e.g. My Office"
                              value={formValue.profileName || ""}
                              onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
                              sx={form.getTextfieldStyle(true)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* Port State  */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Port State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.portState === true && !formValue.isPortScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    portState: true,
                                    isPortScheduleMode: false,
                                  }
                                });
                              }}
                              name="portState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.portState === false && !formValue.isPortScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    portState: false,
                                    isPortScheduleMode: false,
                                  }
                                });
                              }}
                              name="portState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Schedule"
                              value="schedule"
                              checked={formValue.isPortScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    isPortScheduleMode: true,
                                    portState: true,
                                  }
                                });
                              }}
                              name="portState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {
                          formValue.isPortScheduleMode && (
                            <Box sx={{ mt: '10px' }}>
                              <Box className={mainStyle["row-style"]}>
                                <Typography className={mainStyle["collapse-typography"]}>
                                </Typography>
                                <MenuSelect
                                  value={formValue.portSchedule}
                                  onChange={handlePortScheduleChange}
                                  options={scheduleOptions}
                                  placeholder="Select a Schedule Profile"
                                  width={form.getTextfieldStyle().width}
                                  pinningOptions={[
                                    {
                                      value: "CREATE_NEW",
                                      label: "Create New Schedule",
                                      showArrow: true,
                                    }
                                  ]}
                                  disabled={!showEditGeneral}
                                />
                              </Box>
                            </Box>
                          )
                        }
                        {/* Port Type */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Port Type
                            </Typography>
                            {
                              portTypeOption.convertToForm().map((item, index) => (
                                <RadioButton
                                  key={index}
                                  label={item.label}
                                  value={item.value}
                                  checked={formValue.portType === item.value}
                                  onChange={() => {
                                    handleChangeFormValue("allowVlan", originFormValue.allowVlan);
                                    handleChangeFormValue("portType", item.value);
                                  }}
                                  name="portType"
                                  disabled={!showEditGeneral}
                                />
                              ))
                            }
                          </Box>
                        </Box>
                        {/* PVID */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PVID
                            </Typography>
                            <TextField
                              sx={form.getTextfieldStyle(form.checkPvid(formValue.pvid?.toString().trim()))}
                              value={formValue.pvid}
                              onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              (1~4094)
                            </Typography>
                          </Box>
                        </Box>
                        {/* Allow VLAN */}
                        {
                          formValue.portType === portTypeOption.trunk.value && (
                            <>
                              <Box>
                                <Box className={mainStyle["row-style"]}>
                                  <Typography className={mainStyle["collapse-typography"]}>
                                    Allow VLAN
                                  </Typography>
                                  <TextField
                                    placeholder="e.g. 1,4-7"
                                    sx={form.getTextfieldStyle(form.checkAllowVlans(formValue.allowVlan?.trim()))}
                                    value={formValue.allowVlan}
                                    onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                                    disabled={!showEditGeneral}
                                  />
                                </Box>
                              </Box>
                              <Box className={mainStyle["tip_section"]}>
                                <Box className={mainStyle["row-style"]}>
                                  <Typography className={mainStyle["collapse-typography"]}>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: theme.palette.primary.greySecound,
                                      fontSize: theme.typography.customFontSize[1],
                                    }}
                                  >
                                    {/* (1~4094) */}
                                    {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          )
                        }
                        {/* Copper Link Speed */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Copper Link Speed
                            </Typography>
                            <MenuSelect
                              value={formValue.linkSpeed}
                              onChange={(opt) => {
                                handleChangeFormValue("linkSpeed", opt.value);
                              }}
                              options={linkSpeedOption.convertToForm()}
                              width={form.getTextfieldStyle().width}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* Fiber Link Speed */}
                        <Box>
                          <Box className={mainStyle["row-style"]} sx={{ mt: '10px' }}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Fiber Link Speed
                            </Typography>
                            <MenuSelect
                              value={formValue.fiberLinkSpeed}
                              onChange={(opt) => {
                                handleChangeFormValue("fiberLinkSpeed", opt.value);
                              }}
                              options={linkSpeedOption.convertToForm()}
                              width={form.getTextfieldStyle().width}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                    <CollapseCard title="PoE" >
                      <Box className={mainStyle["section-container"]}>
                        {/* PoE State */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PoE State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.poeState === true && !formValue.isPoeScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    poeState: true,
                                    isPoeScheduleMode: false,
                                  }
                                });
                              }}
                              name="poeState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.poeState === false && !formValue.isPoeScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    poeState: false,
                                    isPoeScheduleMode: false,
                                  }
                                });
                              }}
                              name="poeState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Schedule"
                              value="schedule"
                              checked={formValue.isPoeScheduleMode}
                              onChange={() => {
                                handleChangeMultiFormValues({
                                  updateData: {
                                    isPoeScheduleMode: true,
                                    poeState: true,
                                  }
                                });
                              }}
                              name="poeState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {
                          formValue.isPoeScheduleMode && (
                            <Box sx={{ mt: '10px' }}>
                              <Box className={mainStyle["row-style"]}>
                                <Typography className={mainStyle["collapse-typography"]}>
                                </Typography>
                                <MenuSelect
                                  value={formValue.poeSchedule}
                                  onChange={handlePoeScheduleChange}
                                  options={scheduleOptions}
                                  placeholder="Select a PoE Profile"
                                  width={form.getTextfieldStyle().width}
                                  pinningOptions={[
                                    {
                                      value: "CREATE_NEW",
                                      label: "Create New Schedule",
                                      showArrow: true,
                                    }
                                  ]}
                                  disabled={!showEditGeneral}
                                />
                              </Box>
                            </Box>
                          )
                        }
                      </Box>
                    </CollapseCard>
                    <CollapseCard title="Advance" >
                      <Box className={mainStyle["section-container"]}>
                        {/* DDP */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              DDP
                            </Typography>
                            <RadioButton
                              label="Enable"
                              value={true}
                              checked={formValue.ddpState === true}
                              onChange={() => handleChangeFormValue("ddpState", true)}
                              name="ddpState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disable"
                              value={false}
                              checked={formValue.ddpState === false}
                              onChange={() => handleChangeFormValue("ddpState", false)}
                              name="ddpState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* LBD */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              LBD
                            </Typography>
                            <RadioButton
                              label="Enable"
                              value={true}
                              checked={formValue.lbdState === true}
                              onChange={() => handleChangeFormValue("lbdState", true)}
                              name="lbdState"
                              disabled={!showEditGeneral || formValue.rstpState === true}
                            />
                            <RadioButton
                              label="Disable"
                              value={false}
                              checked={formValue.lbdState === false}
                              onChange={() => handleChangeFormValue("lbdState", false)}
                              name="lbdState"
                              disabled={!showEditGeneral || formValue.rstpState === true}
                            />
                          </Box>
                        </Box>
                        {/* RSTP */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]} sx={{ display: "flex", alignItems: "center" }}>
                              RSTP
                              <Tooltip title="When RSTP is enabled as the primary loop prevention mechanism, LBD should not be enabled." placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: 16,
                                    ml: "5px",
                                    "& img": {
                                      content: `url(${icon_snmp})`,
                                    },
                                    "&:hover img": {
                                      content: `url(${icon_snmp_hover})`,
                                    },
                                  }}
                                >
                                  <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                                </Box>
                              </Tooltip>
                            </Typography>
                            <RadioButton
                              label="Enable"
                              value={true}
                              checked={formValue.rstpState === true}
                              onClick={() => handleChangeMultiFormValues({
                                updateData: {
                                  rstpState: true,
                                  lbdState: false,
                                }
                              })}
                              name="rstpState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disable"
                              value={false}
                              checked={formValue.rstpState === false}
                              onClick={() => handleChangeMultiFormValues({
                                updateData: {
                                  rstpState: false,
                                  stpState: false,
                                }
                              })}
                              name="rstpState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* STP Guard */}
                        {formValue.rstpState === true && (
                          <Box>
                            <Box className={mainStyle["row-style"]}>
                              <Typography className={mainStyle["collapse-typography"]}>
                                STP Guard
                              </Typography>
                              <RadioButton
                                label="Enable"
                                value={true}
                                checked={formValue.stpState === true}
                                onChange={() => handleChangeFormValue("stpState", true)}
                                name="stpState"
                                disabled={!showEditGeneral}
                              />
                              <RadioButton
                                label="Disable"
                                value={false}
                                checked={formValue.stpState === false}
                                onChange={() => handleChangeFormValue("stpState", false)}
                                name="stpState"
                                disabled={!showEditGeneral}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Access Policy */}
                        {formValue.portType === portTypeOption.access.value && (
                          <>
                            <Box sx={{ mt: '10px' }}>
                              <Box className={mainStyle["row-style"]}>
                                <Typography className={mainStyle["collapse-typography"]}>
                                  Access Policy
                                  {isUnityProSubscribed || !formValue.accessPolicy?.enableState ? (
                                    <ProColorIcon aria-label="Pro" style={proIconStyle} />
                                  ) : (
                                    <Tooltip
                                      title="Pro feature disabled"
                                      placement="bottom"
                                      arrow
                                    >
                                      <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                                    </Tooltip>
                                  )}
                                </Typography>
                                <MenuSelect
                                  value={
                                    formValue.accessPolicy?.enableState
                                      ? formValue.accessPolicy.type
                                      : accessPolicyTypeDefinition.DISABLED.value
                                  }
                                  onChange={(opt) => {
                                    handleChangeFormValue("accessPolicy", {
                                      ...formValue.accessPolicy,
                                      enableState: opt.value !== accessPolicyTypeDefinition.DISABLED.value,
                                      type: opt.value === accessPolicyTypeDefinition.DISABLED.value
                                        ? accessPolicyTypeDefinition.STATIC.value
                                        : opt.value,
                                    });
                                  }}
                                  options={defaultAccessPolicyTypeList}
                                  width={form.getTextfieldStyle().width}
                                  disabled={!showEditGeneral}
                                />
                              </Box>
                            </Box>

                            {/* Access Policy > Mac Address Section */}
                            {
                              formValue.accessPolicy?.enableState &&
                              (formValue.accessPolicy?.type === accessPolicyTypeDefinition.STATIC.value ||
                                formValue.accessPolicy?.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                                <MacWhitelistSection
                                  macProfile={formValue.accessPolicy.macList || null}
                                  onChangeMacProfile={handleMacProfileChange}
                                  macProfileOptions={macProfileOptions}
                                  macProfilesRaw={macProfiles}
                                  onEditMacProfile={() => {
                                    setLeaveProfileType("mac-address");
                                    setLeaveModalTitle("Edit MAC Address Profile");
                                    setOpenLeaveModal(true);
                                  }}
                                  showEditGeneral={showEditGeneral}
                                  showDynamicSize={formValue.accessPolicy?.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                                  dynamicSize={formValue.accessPolicy?.dynamicSize}
                                  onChangeDynamicSize={(val) => {
                                    handleChangeFormValue("accessPolicy", {
                                      ...formValue.accessPolicy,
                                      dynamicSize: val,
                                    });
                                  }}
                                  form={form}
                                />
                              )
                            }

                            {/* Access Policy > Radius Profiles Section */}
                            {
                              formValue.accessPolicy?.enableState &&
                              formValue.accessPolicy?.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                                <RadiusProfileSection
                                  radiusProfile={
                                    formValue.accessPolicy.radiusProfile || null
                                  }
                                  onChangeRadiusProfile={handleRadiusProfileChange}
                                  radiusProfileOptions={radiusProfileOptions}
                                  radiusProfilesRaw={radiusProfiles}
                                  onEditRadiusProfile={() => {
                                    setLeaveProfileType("radius-server");
                                    setLeaveModalTitle("Edit RADIUS Profile");
                                    setOpenLeaveModal(true);
                                  }}
                                  showEditGeneral={showEditGeneral}
                                />
                              )
                            }
                          </>
                        )}
                      </Box>
                    </CollapseCard>
                  </>
                )
              }
              {
                deviceType === deviceCategoryOption.ap.value && (
                  <>
                    <CollapseCard title="Basic" >
                      <Box className={mainStyle["section-container"]}>
                        {/* Profile Name */}
                        <Box sx={{ mb: '16px' }}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Profile Name
                            </Typography>
                            <TextField
                              placeholder="e.g. My Office"
                              value={formValue.profileName || ""}
                              onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
                              sx={form.getTextfieldStyle(true)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* PVID */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PVID
                            </Typography>
                            <TextField
                              sx={form.getTextfieldStyle(form.checkPvid(formValue.pvid?.toString().trim()))}
                              value={formValue.pvid}
                              onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              (1~4094)
                            </Typography>
                          </Box>
                        </Box>
                        {/* Allow VLAN */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Allow VLAN
                            </Typography>
                            <TextField
                              placeholder="e.g. 1,4-7"
                              sx={form.getTextfieldStyle(form.checkAllowVlans(formValue.allowVlan?.trim()))}
                              value={formValue.allowVlan}
                              onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              {/* (1~4094) */}
                              {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                  </>
                )
              }
              <RemoveButton
                label="Remove Port Profile"
                onHoverDeleteButton={handleHoverDeleteButton}
                isDeleteHovered={isDeleteHovered}
                onOpenRemoveModal={handleOpenRemoveModal}
                showEditGeneral={showEditGeneral}
              />
            </Box>
            <BottomActionBar
              parentId={containerId}
              show={!checkDataNotChanged()}
              onCheckFormValid={checkFormValid}
              onCancel={resetFormValue}
              onApply={handleApply}
            />
          </Box>
          <ConfirmDeleteModal
            removeModal={openRemoveModal}
            onOpenRemoveModal={setOpenRemoveModal}
            label={formValue ? formValue.profileName : ""}
            removeCallback={handleDelete}
          />
          <LeaveConfirmModal
            isOpen={openLeaveModal}
            onClose={() => setOpenLeaveModal(false)}
            leaveCallback={handleLeaveConfirm}
            profileType={leaveProfileType}
            title={leaveModalTitle}
          />
          <LinkModal
            open={isLinkModalOpen}
            onClose={() => setIsLinkModalOpen(false)}
            onLinkClick={() => {
              setIsLinkModalOpen(false);
              const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
              navigate(isMspScope ? "/msp/license" : "/license");
            }}
          />

          <GlobalAlert
            open={alertInfo.open}
            severity={alertInfo.severity}
            message={alertInfo.message}
            onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
          />
        </>
      )}
    </Box>
  );
}

export default PortContent;
